"use client";

import React from "react";
import { MapPin, Phone, Mail, MessageCircle, Scale, ArrowRight, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactInfo = [{
  icon: MapPin,
  title: "Office Address",
  details: ["PG-32, Perdana Shopping Centre, Jalan PJU 8/1, Damansara Perdana, 47820 Petaling Jaya, Selangor"]
}, {
  icon: Phone,
  title: "Phone Enquiries",
  details: ["019-3270771", "\n"]
}, {
  icon: Mail,
  title: "Email Address",
  details: ["shkazrin@gmail.com"]
}];
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 bg-primary/5 border border-border rounded-none text-center h-full min-h-[400px]">
        <Scale className="size-12 text-secondary mb-4" />
        <h3 className="text-2xl font-default font-semibold text-foreground mb-2">Inquiry Received</h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="rounded-none border-border focus-visible:ring-secondary bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-semibold">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="rounded-none border-border focus-visible:ring-secondary bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="legalMatter" rules={{
      required: "Please specify the legal matter"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Legal Matter</FormLabel>
            <FormControl>
              <Input placeholder="e.g., Employment Dispute, Civil Litigation" className="rounded-none border-border focus-visible:ring-secondary bg-background" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="caseDetails" rules={{
      required: "Please provide some details about your case"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-semibold">Case Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your situation..." className="min-h-[150px] rounded-none border-border focus-visible:ring-secondary bg-background resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <div className="flex flex-col sm:flex-row gap-4 pt-2">
        <Button type="submit" disabled={isSubmitting} className="flex-1 rounded-none bg-primary text-primary-foreground hover:bg-primary/90 h-12 text-base font-semibold">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Processing...
            </> : <>
              Request Consultation
              <ArrowRight className="ml-2 size-5" />
            </>}
        </Button>
        
        <Link to="https://wa.me/60193270771" newTab className="flex-1">
          <Button type="button" variant="outline" className="w-full rounded-none border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white h-12 text-base font-semibold transition-colors">
            <MessageCircle className="mr-2 size-5" />
            WhatsApp Us
          </Button>
        </Link>
      </div>
    </Form>;
}
export default function ContactDetails() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="contactdetails" className="bg-background relative overflow-hidden">
      {}
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div ref={headerRef} className={`mb-16 max-w-3xl transition-all duration-1000 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-4 mb-4">
            <div className="h-px w-12 bg-secondary" />
            <span className="text-secondary font-semibold tracking-wider uppercase text-sm">Get In Touch</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-default font-bold text-primary mb-6 leading-tight">
            We listen, analyze <br className="hidden md:block" /> and solve.
          </h2>
          <p className="text-lg text-muted-foreground">
            Schedule a consultation with our experienced legal team. We are ready to provide the strategic guidance and representation you need.
          </p>
        </div>

        <div id="consultation-form" ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-0 shadow-2xl transition-all duration-1000 delay-200 ${isContentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {}
          <div className="lg:col-span-5 bg-primary text-primary-foreground p-10 lg:p-14 flex flex-col justify-between relative overflow-hidden">
            {}
            <div className="absolute -bottom-24 -right-24 opacity-5 pointer-events-none">
              <Scale className="size-96" />
            </div>

            <div className="relative z-10">
              <h3 className="text-2xl font-default font-semibold mb-10 text-secondary">Contact Information</h3>
              
              <div className="space-y-10">
                {contactInfo.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-6 group">
                    <div className="bg-secondary/10 p-4 rounded-none border border-secondary/20 group-hover:bg-secondary/20 transition-colors">
                      <item.icon className="size-6 text-secondary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-primary-foreground/70 uppercase tracking-wider mb-2">{item.title}</h4>
                      {item.details.map((detail, j) => <p key={j} className="text-lg font-medium leading-relaxed">{detail}</p>)}
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="relative z-10 mt-16 pt-10 border-t border-primary-foreground/10">
              <p className="text-sm text-primary-foreground/60">
                Shaik Azrin & Co, Advocates & Solicitors<br />
                Founded in 2004
              </p>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 bg-card p-10 lg:p-14 border border-border lg:border-l-0">
            <div className="mb-8">
              <h3 className="text-2xl font-default font-bold text-foreground mb-2">Request a Consultation</h3>
              <p className="text-muted-foreground">Fill out the form below and our team will get back to you promptly.</p>
            </div>

            <FormProvider formId="consultation_form_home" sectionName="ContactDetails" defaultValues={{
            name: "",
            email: "",
            legalMatter: "",
            caseDetails: ""
          }} submitText="Request Consultation" successMessage="Thank you for reaching out. Our legal team will review your inquiry and contact you shortly.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>

      {}
      
    </section>;
}